import { Auth0ContextInterface } from '@auth0/auth0-react';
import { ASSET_PATHS } from '@lemontaps/lib/constants';
import { Asset, AssetPath } from '@lemontaps/lib/types/api/asset.type';
import { withSilentAccessToken } from '../helper';
import axios from 'axios';
import config from '../../config/config';

type AllocateAssetResponse = {
  presignedUrl: string;
  asset: Asset;
};

export const uploadAsset = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  file: File,
  path: AssetPath = ASSET_PATHS.Font,
  references: { themeConfigReference?: string; jsonColReference?: string } = {},
  onUploadProgress?: (progress: number) => void,
): Promise<Asset> => {
  try {
    // Allocate asset and get presigned URL
    const { data } = await withSilentAccessToken(getAccessTokenSilently, token =>
      axios.post<{ isSuccess: boolean; data: AllocateAssetResponse }>(
        `${config.API_BASE_URL}theme/assets/allocate`,
        {
          path,
          contentType: file.type,
          ...references,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ),
    );

    if (!data.isSuccess) {
      throw new Error('Failed to allocate asset');
    }

    // Upload file using presigned URL
    const response = await axios({
      url: data.data.presignedUrl,
      method: 'put',
      data: file,
      headers: { 'Content-Type': file.type },
      maxContentLength: 100 * 1024 * 1024 * 1024,
      timeout: 30 * 60 * 1000, //30mins
      onUploadProgress: progressEvent => {
        const progress = progressEvent.loaded / progressEvent.total;
        onUploadProgress?.(progress);
      },
    });

    const uploadedUrl = response.config.url?.split('?')[0];
    if (!uploadedUrl) {
      throw new Error('Failed to upload file');
    }

    return data.data.asset;
  } catch (error) {
    console.error('Error allocating asset:', error);
    throw error;
  }
};

export const getAssetsLocation = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  assetIds: string[],
) => {
  const { data } = await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get<{ id: string; location: string }[]>(`${config.API_BASE_URL}theme/assets/location`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        assetIds,
      },
    }),
  );
  return data;
};
